/**
 * Silent refresh, behind a single in-flight Promise mutex.
 *
 * Refresh is single-use with session-version rotation on the backend, so a
 * SECOND concurrent refresh reads as a REPLAY and revokes the whole session.
 * The mutex is therefore correctness, not an optimisation: N parallel 401s
 * must coalesce into exactly one `tokens:refresh` call.
 *
 * **The rotated pair carries no profile, and that is the endpoint's shape
 * rather than a field missing from the wire.** `backend-auth` answers this
 * call with `access_token` and `expires_in`; only `POST /sessions` returns the
 * profile beside them. A reload boots through refresh and never through the
 * sign-in call, so the session used to settle live and nameless and the
 * account menu fell back to its anonymous label for a reader who was signed
 * in. The profile is read from `GET /me` with the token that just landed.
 */
import { rawRequest } from '@shared/api/http';

import {
  clearSession,
  openSession,
  toSessionProfile,
  type SessionProfile,
  type WireSessionProfile,
} from './session';

interface RefreshResult {
  access_token: string;
  /** Not sent by `tokens:refresh` today. Honoured if it ever is. */
  profile?: WireSessionProfile;
}

interface MeResult {
  profile: WireSessionProfile;
}

let inFlight: Promise<boolean> | null = null;

/**
 * Read the account a fresh token belongs to.
 *
 * Goes through `rawRequest` with the token spelled out rather than through
 * `apiClient`: this call happens INSIDE the refresh mutex, and the client's
 * own 401 handling would re-enter the refresh that is still resolving.
 *
 * A failure here is NOT a failed refresh. The token is valid and the session
 * is open; the menu falls back to the anonymous label it was already showing,
 * rather than a reload ejecting a signed-in reader over a display name.
 */
async function readProfile(accessToken: string): Promise<SessionProfile | null> {
  try {
    const me = await rawRequest<MeResult>({
      method: 'GET',
      path: '/me',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return toSessionProfile(me.profile);
  } catch {
    return null;
  }
}

async function callRefresh(): Promise<boolean> {
  try {
    const result = await rawRequest<RefreshResult>({ method: 'POST', path: '/tokens:refresh' });
    const profile = toSessionProfile(result.profile) ?? (await readProfile(result.access_token));
    openSession(result.access_token, profile);
    return true;
  } catch {
    // An expiry and a replay are indistinguishable to the reader by design —
    // do not branch on the code here, and do not add copy that separates them.
    clearSession();
    return false;
  }
}

/** Resolves `true` when a fresh access token is in memory. */
export function refreshOnce(): Promise<boolean> {
  inFlight ??= callRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * A hard refresh has no access token and that is normal: the app boots
 * without one, attempts exactly one refresh against the cookie, and only then
 * lets the guard decide. Skipping this would eject a signed-in subscriber to
 * the sign-in screen on every reload.
 */
export function bootstrapSession(): Promise<boolean> {
  return refreshOnce();
}
